/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2020 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { inject } from "mobx-react";
import React from "react";
import { GlobalNavHelper } from "components/topbar/GlobalNavHelper";
import { getViewMode } from "selectors/viewModeSelectors";
import { isPDF, getShouldUnblockUIFeatures } from "selectors/documentSelectors";
import { isInitialPageLoaded } from "selectors/pagedViewState";
import { searchClickHandler, updateSearchStyle, cssSetterHandler } from "components/topbar/actions/find";
import { onDownloadFileClick } from "components/topbar/actions/downloadFile";
import { getShowSearchInHUD } from "selectors/hudSelectors";
import { getShowSearchInputUI } from "selectors/searchSelectors";
import { getShowSearchInTopbar } from "selectors/topbarSelectors";
import { isReadAloudEnabled, isReadAloudReady, isReadAloudActive } from "selectors/readAloudSelectors";
import {
  isSearchFeatureEnabled,
  isDownloadFeatureEnabled,
  isPrintFeatureEnabled,
  isTextSelectionEnabled,
  getSelectedTool,
  getShouldEnableQnA,
  getIsPreviewMounted,
  getSwitchPreviewState
} from "selectors/appSelectors";
import SearchInputUICss from "components/search/SearchInputUI.css";
import { isCurrentDeviceMobile } from "dc-preview-utils/src/Utils/DeviceUtils";
import { getShouldUseModernViewer, getShouldUseMobileModernViewer } from "selectors/appSelectors";
import { auth2 as auth } from "dc-core";
import { SWITCH_PREVIEW_STATE } from "constants/enums";

const mapStoretoProps = (stores, ownProps) => {
  const viewMode = getViewMode(stores);
  const searchInputUIClassname = getShouldUseModernViewer(stores)
    ? getShouldUseMobileModernViewer(stores)
      ? SearchInputUICss.SearchInputUITopBarModernViewerMobile
      : SearchInputUICss.SearchInputUITopBarModernViewer
    : SearchInputUICss.SearchInputUITopBar;

  return {
    searchClickHandler: () => searchClickHandler(stores),
    cssSetterHandler: customCss => cssSetterHandler(stores, customCss),
    onDownloadFileClick: extraParams => onDownloadFileClick(stores, extraParams),
    updateSearchStyle: () =>
      updateSearchStyle(stores, {
        searchInputClassName: searchInputUIClassname,
        searchWidgetClassName: isCurrentDeviceMobile()
          ? SearchInputUICss.SearchWidgetMobile
          : SearchInputUICss.SearchWidget
      }),
    intl: ownProps.intl,
    viewMode,
    isPDF: isPDF(stores),
    enablePrintFeature: isPrintFeatureEnabled(stores),
    enableQnAFeature: getShouldEnableQnA(stores),
    enableDownloadFeature: isDownloadFeatureEnabled(stores),
    enableReadAloudFeature: isReadAloudEnabled(stores) && isReadAloudReady(stores),
    shouldShowSearchInHud: getShowSearchInHUD(stores),
    shouldShowSeachInTopbar: getShowSearchInTopbar(stores),
    enableSearchFeature: isSearchFeatureEnabled(stores),
    isSearchInputVisible: getShowSearchInputUI(stores),
    isInitialPageLoaded: isInitialPageLoaded(stores),
    isTextSelectionEnabled: isTextSelectionEnabled(stores),
    shouldShowReadAloudInTopbar: isReadAloudActive(stores),
    selectedTool: getSelectedTool(stores),
    shouldUnblockUIFeatures: getShouldUnblockUIFeatures(stores),
    useModernViewer: getShouldUseModernViewer(stores),
    hasAuth: auth && auth.isSignedIn,
    isPreviewMounted: getIsPreviewMounted(stores),
    forceShowFeatures: getSwitchPreviewState(stores) !== SWITCH_PREVIEW_STATE.NONE
  };
};

const Wrap = ({ isPreviewMounted, ...props }) => {
  if (isPreviewMounted) {
    return <GlobalNavHelper {...props} />;
  }
  return null;
};

export const GlobalNavHelperContainer = inject(mapStoretoProps)(Wrap);
